import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchBlogPost, fetchBlogPosts } from '../lib/api';
import type { BlogPost } from '../types';

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-NZ', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Article content is stored as plain text:
 *   - blank line separates blocks
 *   - lines starting with "## " render as h2
 *   - lines starting with "> " render as blockquote
 *   - everything else renders as a paragraph
 */
function ArticleBody({ content }: { content: string }) {
  const blocks = content.split(/\n\s*\n/).map(b => b.trim()).filter(Boolean);
  return (
    <div className="article-body">
      {blocks.map((block, i) => {
        if (block.startsWith('## ')) {
          return <h2 key={i}>{block.slice(3)}</h2>;
        }
        if (block.startsWith('> ')) {
          return <blockquote key={i}>{block.slice(2)}</blockquote>;
        }
        return <p key={i}>{block}</p>;
      })}
    </div>
  );
}

export default function MarineNewsArticle() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [related, setRelated] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    fetchBlogPost(slug).then(p => {
      if (!p) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setPost(p);
      if (p.tags.length > 0) {
        fetchBlogPosts(p.tags[0], 4).then(posts => {
          setRelated(posts.filter(r => r.slug !== p.slug).slice(0, 3));
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });
  }, [slug]);

  if (loading) {
    return <div className="max-w-[820px] mx-auto px-4 md:px-8 py-24 text-center text-text-muted text-sm">Loading…</div>;
  }

  if (notFound || !post) {
    return (
      <div className="max-w-[820px] mx-auto px-4 md:px-8 py-24 text-center">
        <h1 className="font-display text-2xl font-bold mb-3">Story not found</h1>
        <p className="text-text-secondary mb-6">That article may have been moved or unpublished.</p>
        <Link to="/news" className="btn btn-primary btn-sm">Back to Marine News</Link>
      </div>
    );
  }

  return (
    <article className="pb-16">
      <div className="max-w-[820px] mx-auto px-4 md:px-8 pt-10 md:pt-14">
        <div className="flex items-center gap-2 text-sm text-text-muted mb-6">
          <Link to="/" className="text-text-secondary hover:text-accent transition-colors">Home</Link>
          <span>/</span>
          <Link to="/news" className="text-text-secondary hover:text-accent transition-colors">Marine News</Link>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {post.tags.map(t => (
            <Link
              key={t}
              to={`/news?tag=${encodeURIComponent(t)}`}
              className="px-2.5 py-1 rounded-full text-[0.65rem] font-semibold uppercase tracking-wider text-accent border border-[rgba(79,179,217,0.3)] hover:bg-accent hover:text-white transition-colors"
            >
              {t}
            </Link>
          ))}
        </div>

        <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight leading-tight mb-4">{post.title}</h1>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-muted mb-8">
          <span>{post.author}</span>
          <span>·</span>
          <span>{formatDate(post.published_at)}</span>
          {post.source_url && post.source_name && (
            <>
              <span>·</span>
              <span>
                Via{' '}
                <a href={post.source_url} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                  {post.source_name}
                </a>
              </span>
            </>
          )}
        </div>
      </div>

      {/* Full-width hero banner */}
      <div className="w-full mb-10">
        <div className="w-full h-[40vh] min-h-[280px] max-h-[520px]">
          <img src={post.hero_image} alt={post.title} className="w-full h-full object-cover" loading="eager" fetchPriority="high" />
        </div>
      </div>

      <div className="max-w-[820px] mx-auto px-4 md:px-8">
        {post.content && <ArticleBody content={post.content} />}

        <div className="mt-12 pt-6 border-t border-[rgba(79,179,217,0.15)]">
          <Link to="/news" className="text-accent text-sm font-medium hover:underline">← Back to Marine News</Link>
        </div>
      </div>

      {related.length > 0 && (
        <div className="max-w-[1080px] mx-auto px-4 md:px-8 mt-14">
          <h2 className="font-display text-lg md:text-xl font-bold tracking-tight mb-5">More like this</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {related.map(r => (
              <Link
                key={r.id}
                to={`/news/${r.slug}`}
                className="group bg-card-light dark:bg-card border border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)] rounded-xl overflow-hidden"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={r.hero_image} alt={r.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
                </div>
                <div className="p-4">
                  <h3 className="font-display text-sm font-bold leading-snug group-hover:text-accent transition-colors">{r.title}</h3>
                  <span className="text-[0.7rem] text-text-muted">{new Date(r.published_at).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
